import type { CollectionSummaryJSONObject } from '@ikegps/core-api';
import { CollectionModel } from '@ikegps/core-shared';
import { get } from 'lodash';
import { action, computed, makeObservable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { addStyles } from '../../components/theme';
import { T } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { IkePageName } from '../../page/page';
import { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { TagSortState } from '../../state/tag.sort.state';
import { AppStore } from '../../store';
import { scrollSelectedIntoListView } from '../../util/scroll-into-list-view';
import { CaptureBar } from '../capture.bar/capture.bar';
import { CSS as CollectionCardCSS } from '../collection.list/collection.card.style';
import { CollectionList } from '../collection.list/collection.list';
import { CSS as CollectionListCSS } from '../collection.list/collection.list.style';
import { CollectionToolbar } from '../collection.toolbar/collection.toolbar';
import { TagMap } from '../tag.map/tag.map';
import { CSS, Style } from './photomeasure.collection.list.style';
import { PhotomeasureMapOverlay } from './photomeasure.map.overlay';

addStyles(Style);

export class PhotomeasureCollectionList extends IMithrilStateComponent {
	private state: TagState;
	private toolbar: CollectionToolbar;
	private map: TagMap;

	overlay: PhotomeasureMapOverlay;

	@computed get inspectedCapture() {
		const { inspectedCaptureId, inspectedCollectionRevision } = CurrentState;

		if (inspectedCaptureId && inspectedCollectionRevision) {
			return AppStore.Collection.get(inspectedCollectionRevision)?.state.getCapture(inspectedCaptureId);
		}

		return null;
	}

	constructor(state: TagState) {
		super();
		makeObservable(this);

		this.state = state;
		this.toolbar = new CollectionToolbar(this.state, {
			mapZoomOut: false,
			title: T.COLLECTION_TITLE,
		});
		this.map = new TagMap(this.state, 'photomeasureMap');

		this.overlay = new PhotomeasureMapOverlay({
			capture: this.inspectedCapture,
		});

		this.reaction(() => this.collection, this.ensureCollectionSelected);
		this.reaction(
			() => this.inspectedCapture,
			() => {
				const current = get(this.overlay, ['capture', 'objectId'], null);
				const next = get(this, ['inspectedCapture', 'objectId'], null);

				if (next !== current) {
					this.overlay.setCapture(this.inspectedCapture);
				}
			}
		);
		this.reaction(
			() => CurrentState.inspectedCollectionRevision,
			(revision) => {
				if (revision != null) {
					scrollSelectedIntoListView(CollectionListCSS.container, CollectionCardCSS.isInspected);
				}
			}
		);
	}

	get nodes(): MithrilVNode {
		const collectionSummaries = this.visibleCollections;

		if (collectionSummaries == null) {
			return m('div', { className: CSS.container }, [
				m(
					'div',
					{ className: `${CSS.collections.content} ${CSS.collections.isEmpty}` },
					CollectionList.viewLoading(CurrentState.inspectedTagId)
				),
				CaptureBar.view(),
			]);
		}

		const tag = AppStore.Tag.get(CurrentState.inspectedTagId);
		const inspectedCollection = this.collection;

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.collections.container }, [
				m('div', { className: CSS.toolbar.container }, this.toolbar.view()),
				m('div', { className: CSS.collections.content }, [
					CollectionList.view(this.state, tag, collectionSummaries),
					CaptureBar.view(inspectedCollection),
				]),
			]),
			m('div', { className: CSS.map.container }, this.map.view()),
			this.viewMapButton(inspectedCollection),
		]);
	}

	@computed private get visibleCollections(): CollectionSummaryJSONObject[] {
		if (CurrentState.inspectedTagId == null || AppStore.Tag.get(CurrentState.inspectedTagId) == null) {
			return null;
		}

		const tagSummary = AppStore.TagSummary.get(CurrentState.inspectedTagId);
		const revisions = tagSummary != null ? tagSummary.revisions : [];

		const collectionSummaries: CollectionSummaryJSONObject[] = [];
		for (const revision of revisions) {
			const collectionSummary = AppStore.CollectionSummary.get(revision);
			if (collectionSummary != null) {
				collectionSummaries.push(collectionSummary);
			}
		}

		TagSortState.sortSummaries(collectionSummaries);
		CurrentState.Tag.resetLastSelectedCollectionIndex();
		CurrentState.Tag.setExpandedCollectionsUUIDs(collectionSummaries.map((_) => _.uuid));
		return collectionSummaries;
	}

	@action.bound private ensureCollectionSelected() {
		const tagId = CurrentState.inspectedTagId;
		const tagSummary = AppStore.TagSummary.get(tagId);

		CurrentState.setInspectedTagId(tagId);

		if (tagSummary != null && tagSummary.revisions.indexOf(CurrentState.inspectedCollectionRevision) === -1) {
			CurrentState.setInspectedCollection(tagSummary.revisions[0]);
		}
	}

	private viewMapButton(collection: CollectionModel): MithrilVNode {
		const hrefParams = collection != null ? { t: collection.jobId, collection: collection.uuid } : undefined;
		return ImageButton.view(MaterialIcon.MAP, {
			className: CSS.map.button,
			title: T.NAVIGATE_MAP,
			type: 'a',
			href: IkePageName.map,
			hrefParams,
			onclick: (e) => {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				Services.Window.confirmPageChange(e, Services.Route.getRouteURL(IkePageName.map, hrefParams));
			},
		});
	}

	@computed private get collection(): CollectionModel | null {
		if (CurrentState.CollectionEdit.isEditing) {
			return CurrentState.CollectionEdit.collection;
		}

		return AppStore.Collection.get(CurrentState.inspectedCollectionRevision);
	}
}
